import { motion, AnimatePresence } from 'motion/react';
import { ShoppingBag, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useTheme } from '../ThemeProvider';
import { businessTokens } from './businessTheme';
import type { PurchaseCheck } from '../../types/business';

/**
 * "Did you complete your purchase?" — shown when someone comes back to AfroYap
 * after tapping a Store link.
 *
 * Answering yes records the purchase and (rate limit permitting) tells the
 * business owner. Answering no records the decline and sends nothing, which is
 * the point of asking rather than assuming: a tap is interest, not a sale.
 *
 * Dismissing with the X leaves the check pending in Firestore but stops asking,
 * so a person who does not want to answer is not asked again on every resume.
 */
export function PurchaseCheckPrompt({
  check,
  onRespond,
  onDismiss,
}: {
  check: PurchaseCheck | null;
  onRespond: (confirmed: boolean) => void;
  onDismiss: () => void;
}) {
  const { theme } = useTheme();
  const t = businessTokens(theme);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {check && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999998,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            padding: 16,
            paddingBottom: 'max(24px, calc(env(safe-area-inset-bottom) + 16px))',
          }}
          onClick={onDismiss}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 480,
              background: t.surface,
              border: `1px solid ${t.border}`,
              borderRadius: 20,
              padding: 20,
              boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div
                style={{
                  width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                  background: t.accentSoft,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <ShoppingBag style={{ width: 20, height: 20, color: t.accent }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: t.text }}>
                  Did you complete your purchase?
                </p>
                <p
                  style={{
                    margin: '4px 0 0', fontSize: 13, color: t.textMuted,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                  title={check.productTitle}
                >
                  {check.productTitle}
                </p>
              </div>
              <button
                type="button"
                onClick={onDismiss}
                aria-label="Not now"
                style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: t.textMuted }}
              >
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button
                type="button"
                onClick={() => onRespond(false)}
                style={{
                  flex: 1, height: 46, borderRadius: 14, cursor: 'pointer',
                  background: t.inset, color: t.text,
                  border: `1px solid ${t.border}`, fontSize: 15, fontWeight: 600,
                }}
              >
                No
              </button>
              <button
                type="button"
                onClick={() => onRespond(true)}
                style={{
                  flex: 1, height: 46, borderRadius: 14, cursor: 'pointer',
                  background: t.buttonBg, color: t.buttonText,
                  border: 'none', fontSize: 15, fontWeight: 700,
                }}
              >
                Yes, I bought it
              </button>
            </div>

            <p style={{ margin: '12px 0 0', fontSize: 11, color: t.textMuted, textAlign: 'center' }}>
              Answering yes lets the seller know their listing worked. We never share your name.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
